import React, { useState, useRef, useEffect } from 'react'
import { startCase, isEqual } from 'lodash'

import config from 'environment'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_HIGHLIGHT_CARDS } from 'redux/active/actions/types'

import { CardType, COLLECTION_STATUS_NAME } from 'types/deck'
import { Option } from 'components/elements/ArchidektDropdown/types'

import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'
import DeckFilterDropdown from './deckFilterDropdown'
import Icon from 'components/elements/Icon'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'

import styles from './archidektDeckFilter.module.scss'

type Filter = 'keywords' | 'types' | 'collection' | 'colors' | 'colorTags' | 'categories' | 'manaValues'

const defaultFilter: Record<Filter, Record<string, boolean>> = {
  'keywords': {},
  'types': {},
  'collection': {},
  'colors': {},
  'colorTags': {},
  'categories': {},
  'manaValues': {},
}

type Props = {
  className?: string
}

const ArchidektDeckFilter = ({ className }: Props) => {
  const [searchValue, setSearchValue] = useState('')
  const [filters, setFilters] = useState(defaultFilter)

  const inputRef = useRef<any>(null)

  const cardMap = useAppSelector(state => state.deck.cardMap)
  const filteredCards = useAppSelector(state => state.active.filteredCards)

  const [setHighlightCards] = useActionless(SET_HIGHLIGHT_CARDS)

  const handleFilterSelection = (filterKey: Filter, filterValue: string | string[]) => {
    const values = Array.isArray(filterValue) ? filterValue : [filterValue]

    const updatedFilters = {
      ...filters,
      [filterKey]: {
        ...filters[filterKey],
        ...values.reduce((acc, value) => ({ ...acc, [value]: !filters[filterKey]?.[value] }), {}),
      },
    }

    setFilters(updatedFilters)
  }

  const clearAllFilters = () => {
    setFilters(defaultFilter)
    setSearchValue('')
    setHighlightCards([])
  }

  const cardToString = (cardFace: CardType) => {
    let text = cardFace.name

    text += cardFace.superTypes.reduce((acc, type) => (acc += ` ${type} `), '')
    text += cardFace.types.reduce((acc, type) => (acc += ` ${type} `), '')
    text += cardFace.subTypes.reduce((acc, type) => (acc += ` ${type} `), '')
    text += `${cardFace.text} `
    text += cardFace.castingCost.map(cost => `{${cost}}`).join('')

    return text
  }

  useEffect(() => {
    const cards = Object.values(cardMap)
    const filteredCardNames = new Array<string>()

    if (isEqual(filters, defaultFilter) && searchValue.length < 2) {
      setHighlightCards([])
      return
    }

    for (const card of cards) {
      const cardText = card.front && card.back ? cardToString(card.front) + cardToString(card.back) : cardToString(card)
      if (searchValue.length > 2 && !cardText.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) continue

      const cardTypes = [
        ...card.types,
        ...card.subTypes,
        ...card.superTypes,
        ...(card.front?.types || []),
        ...(card.front?.superTypes || []),
        ...(card.front?.subTypes || []),
        ...(card.back?.types || []),
        ...(card.back?.superTypes || []),
        ...(card.back?.subTypes || []),
      ]

      const filteringKeywords = Object.values(filters.keywords).some(status => status)
      const filteringCategories = Object.values(filters.categories).some(status => status)
      const filteringColors = Object.values(filters.colors).some(status => status)
      const filteringTypes = Object.values(filters.types).some(status => status)
      const filteringManaValues = Object.values(filters.manaValues).some(status => status)
      const filteringCollectionStatus = Object.values(filters.collection).some(status => status)
      const filteringColorTag = Object.values(filters.colorTags).some(status => status)

      const keywordMatch = card.keywords.some(keyword => filters.keywords?.[keyword])
      const categoryMatch = card.categories.some(category => filters.categories?.[category])
      const colorMatch =
        card.colors.some(color => filters.colors?.[color]) || (card.colors.length === 0 && filters.colors.Colorless)
      const typeMatch = cardTypes.some(type => filters.types?.[type])

      const manaValueMatch = filters.manaValues?.[`${card.cmc}`]
      const collectionStatusMatch = !!filters.collection?.[COLLECTION_STATUS_NAME[card.owned]]
      const colorTagMatch = !!filters.colorTags?.[card.colorLabel.name]

      if (filteringKeywords && !keywordMatch) continue
      if (filteringCategories && !categoryMatch) continue
      if (filteringColors && !colorMatch) continue
      if (filteringTypes && !typeMatch) continue
      if (filteringManaValues && !manaValueMatch) continue
      if (filteringCollectionStatus && !collectionStatusMatch) continue
      if (filteringColorTag && !colorTagMatch) continue

      filteredCardNames.push(card.name)
    }

    setHighlightCards(filteredCardNames)
  }, [filters, searchValue, cardMap])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const controlKey = config.getOnMac() ? e.metaKey : e.ctrlKey

      if (basicKeybindSkips(e)) return
      if (e.key === '/' && controlKey) {
        inputRef.current.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Helper component to keep logic a bit simpler in the render
  const MetaInfo = () => {
    let filterCount = Object.values(filters).reduce((acc, filter) => {
      return (acc += Object.values(filter).reduce((acc, val) => (acc += val ? 1 : 0), 0))
    }, 0)
    filterCount += 2 < searchValue.length ? 1 : 0

    const filterList: { filterKey: Filter; filterValue: string; label: string }[] = []

    for (const [key, value] of Object.entries(filters)) {
      for (const [subKey, subValue] of Object.entries(value)) {
        if (!subValue) continue

        filterList.push({
          filterKey: key as Filter,
          filterValue: subKey,
          label: `${startCase(key)}: ${subKey}`,
        })
      }
    }

    if (searchValue.length < 2 || filterCount < 1) return null

    return (
      <ArchidektDropdown
        menuClassName={styles.filterListMenu}
        options={[
          { label: 'Clear all', onClick: clearAllFilters, icon: 'close' },
          { type: 'spacer' },
          {
            label: `Text Search: ${searchValue}`,
            className: styles.listedFilter,
            hidden: searchValue.length < 2,
          },
          { type: 'spacer', hidden: searchValue.length < 2 || !filterList.length },
          ...filterList.map(
            ({ label }): Option => ({
              label: label,
              className: styles.listedFilter,
            }),
          ),
        ]}>
        <button className={styles.resetFilters}>
          {filterCount} {filterCount > 1 ? 'filters' : 'filter'} (
          {filteredCards.length ? `${filteredCards.length} cards` : 'No results found'})
          <Icon name="caret down" />
        </button>
      </ArchidektDropdown>
    )
  }

  // We do this so we aren't opening the search by mistake
  // I don't like that we need this in this component, feels like a separation issue tbh
  const handleIgnoreEnter = (e: React.KeyboardEvent) => (e.key === 'Enter' ? e.preventDefault() : null)

  return (
    <div className={styles.row}>
      <div className={`${styles.container} ${className}`}>
        <label className={styles.filterHeader}>
          <span>Local filter</span>
          <MetaInfo />
        </label>
        <input
          ref={inputRef}
          className={styles.input}
          autoCorrect="off"
          autoComplete="off"
          onFocus={() => inputRef.current.select()}
          placeholder={'Filter deck (eg: Sol Ring)'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
          onKeyDown={handleIgnoreEnter}
          value={searchValue}
        />
        <Icon name="search" className={styles.searchIcon} />
      </div>
      <DeckFilterDropdown handleFilterSelection={handleFilterSelection} setFilters={setFilters} filters={filters} />
    </div>
  )
}

export default ArchidektDeckFilter
